import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiChevronDown } from 'react-icons/fi';
import CookiesMessage from '../../components/CookiesMessage/CookiesMessage';
import Toast from '../../components/Toast';
import { createSession, navigationUrl } from '../../store/analytic/actions';
import { setCPF as setCPFRedux } from '../../store/auth/actions';
import { setTipoAcesso } from '../../store/config/actions';
import { getContacts } from '../../store/contacts/actions';
import { getUserNameByCpf } from '../../store/contactsByCpf/actions';
import format from '../../services/utils/formatCPFandCNPJ';
import validateCnpj from '../../services/utils/validateCnpj';
import validateCpf from '../../services/utils/validateCpf';
import { handleAcceptCookies } from '../../utils/handleAcceptCookies';
import { faqData } from './constants';
import Logo1 from '../../assets/icons/logo_1_meireles.svg';
import Logo2 from '../../assets/icons/logo_2_meireles.svg';

import {
  PageContainer,
  HeaderSection,
  FormWrapper,
  LoginCard,
  FormLogo,
  FormBox,
  FormTitle,
  FormInstruction,
  ButtonArea,
  FormInput,
  FormButton,
  BenefitsSection,
  SectionTitle,
  BenefitsGrid,
  BenefitCard,
  BenefitIcon,
  BenefitTitle,
  BenefitDescription,
  StepsSection,
  StepsContainer,
  Step,
  StepCircle,
  StepContent,
  StepTitle,
  StepDescription,
  PartnerSection,
  PartnerContent,
  PartnerInfo,
  PartnerLogo,
  FaqSection,
  AccordionItem,
  AccordionHeader,
  AccordionContent,
  Footer,
  FooterContent,
  FooterColumn,
  PaymentIcons,
  BankPixIcon,
  CleanNameIcon,
  FastNegotiationIcon,
  SecurityIcon,
  ChevronWrapper,
  MeirelesPixIcon,
  CreditCardIcon,
  BarCodeIcon,
  PaymentIconsWrapper,
  PartnerImage,
} from './styles';

const FillCpf: React.FC = () => {
  const [cpf, setCpf] = useState('');
  const [validCpf, setValidCpf] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const {
    guid,
    validacaoAcesso,
    assessoriaImage,
    imagemCentralHome,
    tituloSite,
  } = useSelector((state: ApplicationState) => ({
    guid: state.analytic.guid,
    validacaoAcesso: state.config.validacaoAcesso,
    assessoriaImage: state.config.assessoriaImage,
    imagemCentralHome: state.config.imagemCentralHome,
    tituloSite: state.config.tituloSite,
  }));

  const dispatch = useDispatch();
  const history = useHistory();
  const currentUrl = window.location.href;

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (tituloSite) {
      document.title = tituloSite;
    } else {
      document.title = 'Portal de Negociação';
    }
  }, [tituloSite]);

  const validaCpfCnpj = useCallback((val: string) => {
    if (!val) return;
    const numericValue = val.replace(/\D/g, '');

    if (numericValue.length > 0) {
      if (numericValue.length <= 11) {
        setValidCpf(validateCpf(numericValue));
      } else if (numericValue.length === 14) {
        setValidCpf(validateCnpj(numericValue));
      } else {
        setValidCpf(false);
      }
    } else {
      setValidCpf(false);
    }
  }, []);

  useEffect(() => {
    validaCpfCnpj(cpf);
  }, [cpf, validaCpfCnpj]);

  useEffect(() => {
    if (!guid || guid.length <= 0) {
      dispatch(createSession.request(currentUrl));
    }
    dispatch(setTipoAcesso(1));
  }, [currentUrl, dispatch, guid]);

  useEffect(() => {
    if (guid.length > 0) {
      dispatch(navigationUrl.request({ action: '/acessouSite' }));
    }
  }, [guid, dispatch]);

  const handleSendCPF = () => {
    handleAcceptCookies();
    if (!validCpf) {
      return toast.error(
        <Toast
          title='Dados inválidos!'
          description='Por favor, insira um CPF ou CNPJ válido para continuar.'
        />
      );
    }

    if (validacaoAcesso === '23' || validacaoAcesso === '25') {
      dispatch(getContacts.request({ cpf }));
    } else if (['24', '26', '27'].includes(validacaoAcesso)) {
      dispatch(getUserNameByCpf.request({ cpf }));
    }

    dispatch(setCPFRedux(cpf));
    history.push('/contato');
  };

  const handleToggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <PageContainer>
      <HeaderSection bgImage={imagemCentralHome}>
        <FormLogo src={Logo1} alt='Logo da Parceria' />
        <FormWrapper>
          <LoginCard>
            <FormBox>
              <FormTitle>
                Quer resolver suas dívidas de forma rápida e fácil? Acesse agora
                e descubra as melhores ofertas pra você!
              </FormTitle>
              <FormInstruction>
                Digite seu CPF/CNPJ para iniciar a pesquisa
              </FormInstruction>
              <FormInput
                type='tel'
                placeholder='CPF/CNPJ'
                value={format(cpf)}
                onChange={(e) => setCpf(e.target.value.replace(/\D/g, ''))}
                onKeyPress={(e) => e.key === 'Enter' && handleSendCPF()}
                maxLength={18}
              />
              <CookiesMessage />
            </FormBox>
            <ButtonArea>
              <FormButton onClick={handleSendCPF}>Buscar</FormButton>
            </ButtonArea>
          </LoginCard>
        </FormWrapper>
      </HeaderSection>

      <BenefitsSection>
        <SectionTitle>Benefícios ao renegociar</SectionTitle>
        <BenefitsGrid>
          <BenefitCard>
            <BenefitIcon>
              <CleanNameIcon />
            </BenefitIcon>
            <BenefitTitle>Nome limpo em 5 dias</BenefitTitle>
            <BenefitDescription>
              Limpe seu nome após o pagamento.
            </BenefitDescription>
          </BenefitCard>
          <BenefitCard>
            <BenefitIcon>
              <BankPixIcon />
            </BenefitIcon>
            <BenefitTitle>Pague com Pix no App do Seu Banco</BenefitTitle>
            <BenefitDescription>
              Use o aplicativo que você já conhece e pague com um clique, sem
              complicações.
            </BenefitDescription>
          </BenefitCard>
          <BenefitCard>
            <BenefitIcon>
              <SecurityIcon />
            </BenefitIcon>
            <BenefitTitle>Agilidade e Segurança</BenefitTitle>
            <BenefitDescription>
              Ambiente protegido e negociação concluída em poucos minutos.
            </BenefitDescription>
          </BenefitCard>
          <BenefitCard>
            <BenefitIcon>
              <FastNegotiationIcon />
            </BenefitIcon>
            <BenefitTitle>
              Negocie de Forma Rápida, Fácil e a qualquer hora
            </BenefitTitle>
            <BenefitDescription>
              Simule, escolha a melhor opção e finalize em instantes, com acesso
              24 horas por dia, 7 dias por semana.
            </BenefitDescription>
          </BenefitCard>
        </BenefitsGrid>
      </BenefitsSection>

      <StepsSection>
        <SectionTitle dark>
          Resolva agora de forma simples e rápida!
        </SectionTitle>
        <StepsContainer>
          <Step>
            <StepCircle>1</StepCircle>
            <StepContent>
              <StepTitle>Pesquisa</StepTitle>
              <StepDescription>Informe seu CPF/CNPJ.</StepDescription>
            </StepContent>
          </Step>

          <Step>
            <StepCircle>2</StepCircle>
            <StepContent>
              <StepTitle>Confirmação</StepTitle>
              <StepDescription>
                Consulte seu contrato e confirme seus dados.
              </StepDescription>
            </StepContent>
          </Step>

          <Step>
            <StepCircle>3</StepCircle>
            <StepContent>
              <StepTitle>Proposta</StepTitle>
              <StepDescription>
                Confira a proposta exclusiva que preparamos para você.
              </StepDescription>
            </StepContent>
          </Step>

          <Step>
            <StepCircle>4</StepCircle>
            <StepContent>
              <StepTitle>Acordo</StepTitle>
              <StepDescription>
                Confira o acordo, verifique as opções de pagamento e conclua sua
                negociação.
              </StepDescription>
            </StepContent>
          </Step>
        </StepsContainer>
      </StepsSection>

      <PartnerSection bgImage={!isMobile ? assessoriaImage : undefined}>
        <PartnerContent>
          <PartnerInfo>
            <h2>Somos uma empresa parceira da Sabesp</h2>
            <div>
              <p>
                Com mais de 45 anos de experiência, a Meireles e Freitas é
                referencia nacional em Gestão de Relacionamento, cobrança e
                serviços de campo com integração de ponta a ponta.
              </p>
              <p>
                São milhares de funcionários espalhados em todas as regiões do
                país, focados em realizar todas as atividades com excelência,
                superando expectativas de clientes e consumidores.
              </p>
              <p>
                Os reconhecidos resultados vêm de uma política de alto
                investimento no colaborador, amplamente reconhecida em prêmios
                nacionais e internacionais.
              </p>
            </div>
            <PartnerLogo src={Logo2} alt='Logo Secundário Meireles e Freitas' />
          </PartnerInfo>
          {isMobile && (
            <PartnerImage src={assessoriaImage} alt='Imagem Trio Meireles' />
          )}
        </PartnerContent>
      </PartnerSection>

      <FaqSection>
        <SectionTitle style={{ color: '#45C1EF' }}>
          Perguntas Frequentes
        </SectionTitle>
        {faqData.map((faq, index) => (
          <AccordionItem key={index}>
            <AccordionHeader onClick={() => handleToggleFaq(index)}>
              <p>{faq.title}</p>
              <ChevronWrapper>
                <FiChevronDown
                  style={{
                    transform:
                      openFaq === index ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s',
                  }}
                  size={45}
                  color='#003854'
                />
              </ChevronWrapper>
            </AccordionHeader>
            <AccordionContent isOpen={openFaq === index}>
              <p dangerouslySetInnerHTML={{ __html: faq.description }} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </FaqSection>

      <Footer>
        <FooterContent>
          <FooterColumn>
            <h4>Sobre nós</h4>
            <p>
              Meireles e Freitas Serviços de Cobranças Ltda
              <br /> <br />
              CNPJ 07.777.738/0001-09.
            </p>
          </FooterColumn>
          <FooterColumn>
            <h4>Formas de pagamento</h4>
            <PaymentIcons>
              <PaymentIconsWrapper>
                <MeirelesPixIcon />
              </PaymentIconsWrapper>
              <PaymentIconsWrapper>
                <CreditCardIcon />
              </PaymentIconsWrapper>
              <PaymentIconsWrapper>
                <BarCodeIcon />
              </PaymentIconsWrapper>
            </PaymentIcons>
          </FooterColumn>
        </FooterContent>
      </Footer>
    </PageContainer>
  );
};

export default FillCpf;
